import { getTranslations, translateBackend } from '@assets/i18n/config';
import {
  ASSET_CLASS_FIELD_BY_LEVEL,
  type AssetClassBranch,
  type AssetClassLevel,
  type PortfolioTemplate,
  Strategy,
} from '@interfaces/financialProduct';
import type {
  Security,
  SecurityTableOptions,
} from '@ovComponents/3-pattern/securitiesSelectionTable/securitiesSelectionTable';
import { assignSecurityColors } from '@ovComponents/4-module/workflowCompletion/subSteps/utils/assignSecurityColors';
import {
  type AssetClassGroup,
  getTaxRankValidation,
} from '@ovComponents/4-module/workflowCompletion/subSteps/utils/useAssetClassGrouping';
import type {
  HouseholdModelPortfolioChild,
  HouseholdModelPortfolioModel,
} from './householdModelPortfolio.types';

const DEFAULT_SECURITY_COLOR = 'supportOne' as const;

export type HouseholdModelPortfolioPieChartProps = {
  securities: Security[];
  modelPortfolioName?: string;
  portfolioTemplate?: PortfolioTemplate;
  assetClassLevel?: AssetClassLevel | string;
};

export type HouseholdModelPortfolioAllocationData = {
  securities: Security[];
  tableSecurities: Security[];
  isAssetClassStrategy: boolean;
  groupedSecurities: AssetClassGroup[] | null;
  unassignedSecurities: Security[];
  pieChartProps: HouseholdModelPortfolioPieChartProps;
  tableOptions: SecurityTableOptions;
};

export const isHouseholdModelPortfolioAssetClassStrategy = (
  model: HouseholdModelPortfolioModel | null | undefined,
): boolean => model?.strategy === Strategy.ASSET_CLASS;

/** Maps query children to `Security[]` for read-only allocation components. */
export const mapHouseholdModelPortfolioChildrenToSecurities = (
  children: HouseholdModelPortfolioChild[] | undefined,
): Security[] => {
  if (!children?.length) {
    return [];
  }

  return children
    .filter(
      (
        child,
      ): child is HouseholdModelPortfolioChild & {
        financialProduct: NonNullable<
          HouseholdModelPortfolioChild['financialProduct']
        > & { id: string };
      } => Boolean(child.financialProduct?.id),
    )
    .map((child): Security => {
      const financialProduct = child.financialProduct;

      return {
        id: child.id ?? financialProduct.id,
        percentage: child.percentage ?? 0,
        taxRank: child.taxRank ?? financialProduct.taxRank ?? undefined,
        isCashEquivalent: child.isCashEquivalent ?? false,
        assetClass: child.assetClass,
        financialProduct: {
          id: financialProduct.id,
          name: financialProduct.name ?? '',
          ticker: financialProduct.ticker ?? undefined,
          isCash: financialProduct.isCash ?? false,
          taxRank: financialProduct.taxRank ?? undefined,
          translatedName: financialProduct.translatedName,
          primaryAssetClass: financialProduct.primaryAssetClass,
          secondaryAssetClass: financialProduct.secondaryAssetClass,
          tertiaryAssetClass: financialProduct.tertiaryAssetClass,
        },
        color: DEFAULT_SECURITY_COLOR,
      };
    });
};

export const getHouseholdModelPortfolioSecurities = (
  model: HouseholdModelPortfolioModel | null | undefined,
): Security[] =>
  assignSecurityColors(
    mapHouseholdModelPortfolioChildrenToSecurities(model?.children),
  );

/**
 * Pure grouping helper mirroring `useAssetClassGrouping` for read-only tables.
 * Keeps allocation section testable without rendering hooks.
 */
export const groupHouseholdModelPortfolioSecuritiesByAssetClass = (
  isAssetClass: boolean,
  securities: Security[],
  assetClassBranches: AssetClassBranch[],
  assetClassLevel: AssetClassLevel | string | undefined,
): {
  groupedSecurities: AssetClassGroup[] | null;
  unassignedSecurities: Security[];
} => {
  if (!isAssetClass) {
    return { groupedSecurities: null, unassignedSecurities: [] };
  }

  const assetClassField =
    ASSET_CLASS_FIELD_BY_LEVEL[assetClassLevel as AssetClassLevel] ??
    ASSET_CLASS_FIELD_BY_LEVEL.PRIMARY;

  const groupedSecurities = assetClassBranches
    .filter((branch) => branch.assetClass?.id)
    .map((branch) => ({
      branch,
      securities: securities.filter(
        (security) =>
          security.financialProduct[assetClassField]?.id ===
          branch.assetClass?.id,
      ),
    }));

  const assignedIds = new Set(
    groupedSecurities.flatMap(({ securities: groupSecurities }) =>
      groupSecurities.map((security) => security.financialProduct.id),
    ),
  );

  const unassignedSecurities = securities.filter(
    (security) => !assignedIds.has(security.financialProduct.id),
  );

  return { groupedSecurities, unassignedSecurities };
};

export const getHouseholdModelPortfolioAllocationTableOptions =
  (): SecurityTableOptions => {
    const secColTranslations = (key: string) =>
      getTranslations({
        fileName: 'modelPortfolios',
        translationPath: ['securityTableColumns', key],
      });

    const tableColumns = {
      security: {
        enabled: true,
        label: secColTranslations('security'),
      },
      assetClass: {
        enabled: true,
        label: secColTranslations('assetClass'),
      },
      taxRank: { enabled: true, label: secColTranslations('taxRank') },
      targetPercentage: {
        enabled: true,
        label: secColTranslations('targetPercentage'),
      },
      buttonRemoveSecurity: { enabled: false },
    };

    return {
      securityAllocationChart: { columns: [tableColumns] },
    };
  };

/** Pie chart props for Standard vs Asset Grouped strategies (mirrors admin page). */
export const getHouseholdModelPortfolioPieChartProps = (
  model: HouseholdModelPortfolioModel | null | undefined,
  securities: Security[],
): HouseholdModelPortfolioPieChartProps => {
  if (!model) {
    return { securities: [] };
  }

  const isAssetClass = isHouseholdModelPortfolioAssetClassStrategy(model);
  const assetClassBranches = model.assetClassBranches ?? [];
  const modelPortfolioName = translateBackend(model.translatedName);

  if (!isAssetClass) {
    return {
      securities,
      modelPortfolioName,
      portfolioTemplate: model.portfolioTemplate as
        | PortfolioTemplate
        | undefined,
    };
  }

  const portfolioTemplate =
    assetClassBranches.length > 0
      ? ({
          ...model.portfolioTemplate,
          assetClassBranches,
        } as PortfolioTemplate)
      : (model.portfolioTemplate as PortfolioTemplate | undefined);

  return {
    securities: [],
    modelPortfolioName,
    portfolioTemplate,
    assetClassLevel: model.assetClassLevel,
  };
};

/** Single entry point for read-only allocation donut + table wiring in commit 3. */
export const getHouseholdModelPortfolioAllocationData = (
  model: HouseholdModelPortfolioModel | null | undefined,
): HouseholdModelPortfolioAllocationData => {
  const isAssetClassStrategy =
    isHouseholdModelPortfolioAssetClassStrategy(model);
  const securities = getHouseholdModelPortfolioSecurities(model);
  const { securitiesWithRanks } = getTaxRankValidation(
    securities,
    isAssetClassStrategy,
  );
  const tableSecurities = isAssetClassStrategy
    ? securitiesWithRanks
    : securities;
  const { groupedSecurities, unassignedSecurities } =
    groupHouseholdModelPortfolioSecuritiesByAssetClass(
      isAssetClassStrategy,
      tableSecurities,
      model?.assetClassBranches ?? [],
      model?.assetClassLevel,
    );

  return {
    securities,
    tableSecurities,
    isAssetClassStrategy,
    groupedSecurities,
    unassignedSecurities,
    pieChartProps: getHouseholdModelPortfolioPieChartProps(model, securities),
    tableOptions: getHouseholdModelPortfolioAllocationTableOptions(),
  };
};
